import { GetServerSideProps } from 'next';
import { getMerchGroups } from '@components/shared/shop/model/MerchGroup';
import {
  getSelectedShopGroupChain,
  getTopLevelShopGroups,
  setSelectedShopGroup
} from '@components/shared/shop/model/ShopGroup';
import { addApolloState } from '@lib/apollo/apolloClient';
import formatShopSuggestions
  from '@components/pages/ShopV3/components/ShopSuggestions/formatShopSuggestions';
import {
  fetchShopNavigationMenu,
  IShopNavigationMenu
} from '@components/shared/shop/ShopNavigationMenu';
import { getQueryParam } from '@lib/url';
import {
  IFeaturedItem,
  IShopPageData,
  ShopPageType
} from '@components/pages/ShopV3/model/ShopPage';
import fetchPrismicDocument from '@lib/prismic/fetchPrismicDocument';
import { asText, optionalImage } from '@lib/prismic/util/document';
import logger from '@lib/logger';
import {
  createSearchCriteria,
  getApolloForSearch,
  parseFacets,
  productSearch
} from '@components/pages/ShopV3/util/search';
import { allFulfilled } from '@lib/promise';
import { ApolloClient } from '@apollo/client';
import GET_MERCH_GROUPS_BY_IDS
  from '@components/pages/ShopLanding/graphql/merch-groups-by-ids.graphql';
import { getFormattedMerchGroupUrl } from '@components/pages/ShopLanding/model/prismic';
import {
  getMerchGroupCategoryBySlug,
  IMerchGroupCategory
} from '@components/shared/shop/model/MerchGroupCategory';
import ShopPage from '@components/pages/ShopV3/ShopPage';

const log = logger('ShopMerchGroupCategory');

export default ShopPage;

export const getServerSideProps: GetServerSideProps = async (context) => {
  const {
    query,
    params,
    previewData,
  } = context;

  const slug = getQueryParam(params!, 'slug')!;

  const apolloClient = getApolloForSearch();

  const [
    merchGroups,
    shopGroupsResult,
    selectedMerchGroupCategory,
  ] = await allFulfilled([
    { name: 'getMerchGroups', promise: getMerchGroups(apolloClient) },
    { name: 'getTopLevelShopGroups', promise: getTopLevelShopGroups(apolloClient) },
    { name: 'getMerchGroupCategoryBySlug', promise: getMerchGroupCategoryBySlug(apolloClient, slug) },
  ], log);

  if (!selectedMerchGroupCategory) {
    return { notFound: true };
  }

  const shopGroups = setSelectedShopGroup(shopGroupsResult, selectedMerchGroupCategory);

  const shopPageData = await getMerchGroupCategoryShopPageData(
    selectedMerchGroupCategory, previewData, apolloClient
  );

  const searchCriteria = createSearchCriteria({
    query,
    selectedMerchGroupCategory,
  });

  const [searchResponse, shopNavigationMenu] = await allFulfilled([
    productSearch(apolloClient, searchCriteria),
    fetchShopNavigationMenu({
      previewData,
      apolloClient,
      merchGroups,
      merchGroupCategories: shopGroups,
    })
  ]);

  return addApolloState(apolloClient, {
    props: {
      query: searchCriteria.query || null,
      slugs: [slug] || null,
      shopPageType: ShopPageType.MERCH_GROUP_CATEGORY,
      searchCriteria,
      searchFilters: {
        shopGroups,
        selectedMerchGroup: null,
        selectedShopGroupChain: getSelectedShopGroupChain(shopGroups) || [],
        selectedVendorGroupChain: [],
      },
      searchResults: {
        ...searchResponse.data.searchShopProducts,
        facets: parseFacets(searchResponse.data.searchShopProducts.facets),
      },
      shopPageData,
      shopNavigationMenu,
      footerImages: formatShopSuggestions(
        shopNavigationMenu.map((m: IShopNavigationMenu) => m.option)
      ),
    },
  });
};

async function getMerchGroupCategoryShopPageData(
  selectedMerchGroupCategory: IMerchGroupCategory,
  previewData: any,
  apolloClient: ApolloClient<any>
) {
  const shopPageData: IShopPageData = {
    title: selectedMerchGroupCategory!.title,
    description: selectedMerchGroupCategory.description,
    featuredItems: [],
  };
  try {
    const prismicDoc = await fetchPrismicDocument(
      'merch_group_category',
      selectedMerchGroupCategory.slug,
      { previewData }
    );

    if (prismicDoc?.data) {
      const { data } = prismicDoc;

      const merchGroupsByIds = await apolloClient.query({
        query: GET_MERCH_GROUPS_BY_IDS,
        variables: {
          merchGroupIds: data.collection_features
            .map((m: any) => m.merch_group_id)
            .filter((mgId: string) => !!mgId)
        }
      });

      shopPageData.description = asText(data.description, shopPageData.description);
      shopPageData.image = optionalImage(data.hero_image);

      shopPageData.featuredItems = data.collection_features.map((feature: any) => {
        const featuredItem: IFeaturedItem = {
          title: asText(feature.feature_title),
          description: asText(feature.feature_description),
          image: optionalImage(feature.feature_image),
        };
        if (feature.feature_cta_text && feature.merch_group_id) {
          featuredItem.cta = {
            text: feature.feature_cta_text,
            url: getFormattedMerchGroupUrl(merchGroupsByIds, feature.merch_group_id),
          };
        }
        return featuredItem;
      });
    } else {
      log.debug({
        merchGroupCategory: selectedMerchGroupCategory.slug
      }, 'No Prismic data found for merch group category');
    }
  } catch (ex) {
    log.warn({
      merchGroupCategory: selectedMerchGroupCategory.slug
    },
    'Unable to retrieve Prismic data for merch group category',
    ex);
  }

  return shopPageData;
}
